import React, { useCallback, useMemo } from 'react';
import { useLocalSearchParams } from 'expo-router';

import { LoadingAnimation } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry, useTrackEventOnFocus } from '@order/Telemetry';

import { NotFoundView } from '../../components';
import { useOneClickAddToBag } from '../../hooks';
import { ProductDetailsReady } from './components';
import {
  useProductDetailsContent,
  useProductDetailsLocation,
  useProductDetailsNavigation,
  useProductDetailsRemoteData,
} from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsScreen = () => {
  const isFeatureEnabled = useFeatureFlag('CELS-4497-order-product-details-page-enabled'); // prettier-ignore

  if (isFeatureEnabled) {
    return <ProductDetailsScreenEnabled />;
  }

  return <ProductDetailsScreenDisabled />;
};

// ─────────────────────────────────────────────────────────────────────────────

const ProductDetailsScreenEnabled = () => {
  const { track } = useTelemetry();

  // ─── Route ───────────────────────────────────────────────────────────

  const { baseProductSlug } = useLocalSearchParams<{ baseProductSlug: string }>();

  // ─── Remote Data ─────────────────────────────────────────────────────

  const content = useProductDetailsContent({ baseProductSlug });
  const location = useProductDetailsLocation();

  const lastInteractedStore = location.lastInteractedStore;
  const restaurantId = lastInteractedStore?.restaurantId;
  const deliveryAddressId = location.lastInteractedStore?.addressId;
  const isDeliveryAddress = Boolean(deliveryAddressId);

  const remoteData = useProductDetailsRemoteData({ baseProductSlug, restaurantId, deliveryAddressId }); // prettier-ignore

  const {
    orderChannel,
    baseProduct,
    productForRestaurant,
    locationName,
    deliveryDetailsData,
    restaurantData,
  } = remoteData;

  // ─────────────────────────────────────────────────────────────────────

  const telemetryPayload = useMemo(() => {
    return {
      baseProductSlug,
      productId: baseProduct?.id,
      productName: baseProduct?.name,
    };
  }, [baseProduct?.id, baseProduct?.name, baseProductSlug]);

  // ─── Navigation ──────────────────────────────────────────────────────

  const { navigateBack, navigateToLocationSearch, navigateToCustomization } =
    useProductDetailsNavigation({
      lastInteractedStore,
      baseProductSlug,
      productForRestaurant,
    });

  // ─── Quick Add To Bag ────────────────────────────────────────────────

  const onOneClickAddToBagSuccess = useCallback<OnOneClickAddToBagSuccess>(
    (selectedProduct) => {
      track('product-details.quick-add-to-bag.success', { selectedProduct });
    },
    [track],
  );

  const onOneClickAddToBagError = useCallback<OnOneClickAddToBagError>(
    (errorMessage) => {
      track('product-details.quick-add-to-bag.error', { errorMessage });
    },
    [track],
  );

  const { addProductToBag, isAddingProductToBag } = useOneClickAddToBag({
    restaurantSlug: restaurantData?.slug ?? '',
    deliveryDetails: deliveryDetailsData ?? undefined,
    onSuccess: onOneClickAddToBagSuccess,
    onError: onOneClickAddToBagError,
  });

  // ─── Flags ───────────────────────────────────────────────────────────

  const hasNoSelectedLocation = !lastInteractedStore;
  const isUnavailableProduct =
    Boolean(lastInteractedStore && !productForRestaurant) ||
    productForRestaurant?.outOfStock === true;

  const isModifiableProduct = productForRestaurant?.isModifiable === true;

  const shouldRenderLoadingView = content.fetching || remoteData.isFetching;

  const shouldRenderNotFoundView =
    !baseProduct || !content.hasRemoteData || (isDeliveryAddress && !deliveryDetailsData);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onNavigateToCustomization = useCallback(() => {
    track('product-details.add-to-bag-tapped', telemetryPayload);

    navigateToCustomization();
  }, [navigateToCustomization, telemetryPayload, track]);

  const onQuickAddToBag = useCallback(() => {
    if (!productForRestaurant) return;

    track('product-details.quick-add-to-bag-tapped', telemetryPayload);

    void addProductToBag(productForRestaurant, 1);
  }, [addProductToBag, productForRestaurant, telemetryPayload, track]);

  const handleNavigateToLocationSearch = useCallback(() => {
    track('product-details.change-location-tapped', telemetryPayload);

    navigateToLocationSearch();
  }, [navigateToLocationSearch, telemetryPayload, track]);

  const trackErrorView = useCallback(() => {
    const noBaseProduct = 'Cannot resolve base product';
    const noDeliveryDetails = 'Cannot resolve delivery details';

    if (!baseProduct) {
      track('product-details.error-viewed', { baseProductSlug, reason: noBaseProduct }); // prettier-ignore

      return;
    }

    if (isDeliveryAddress && !deliveryDetailsData) {
      track('product-details.error-viewed', { baseProductSlug, reason: noDeliveryDetails }); // prettier-ignore

      return;
    }

    track('product-details.error-viewed', { baseProductSlug });
  }, [baseProduct, baseProductSlug, deliveryDetailsData, isDeliveryAddress, track]);

  const onAddToBag =
    isModifiableProduct || isUnavailableProduct ? onNavigateToCustomization : onQuickAddToBag;

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEventOnFocus(
    { name: 'product-details.viewed', payload: telemetryPayload },
    { skip: shouldRenderLoadingView || shouldRenderNotFoundView },
  );

  // ─────────────────────────────────────────────────────────────────────

  if (shouldRenderLoadingView) {
    return <LoadingAnimation />;
  }

  if (shouldRenderNotFoundView) {
    return <NotFoundView onMount={trackErrorView} />;
  }

  return (
    <ProductDetailsReady
      baseProduct={baseProduct}
      productCost={productForRestaurant?.cost}
      restaurantData={restaurantData}
      deliveryDetailsData={deliveryDetailsData}
      orderChannel={orderChannel}
      locationName={locationName}
      content={content.mappedContent}
      isUnavailableProduct={isUnavailableProduct}
      hasNoSelectedLocation={hasNoSelectedLocation}
      isAddingToBag={isAddingProductToBag}
      navigateBack={navigateBack}
      navigateToLocationSearch={handleNavigateToLocationSearch}
      onAddToBag={onAddToBag}
    />
  );
};

const ProductDetailsScreenDisabled = () => {
  return <NotFoundView />;
};

// ─── Types ───────────────────────────────────────────────────────────────────

type OnOneClickAddToBagSuccess = NonNullable<OneClickAddToBagParams['onSuccess']>; // prettier-ignore
type OnOneClickAddToBagError = NonNullable<OneClickAddToBagParams['onError']>; // prettier-ignore

type OneClickAddToBagParams = Parameters<typeof useOneClickAddToBag>['0'];
